// The app-wide UI vocabulary.
//
// Everything here reads colour from tokens (`var(--…)`) and never from a hex
// literal, which is what lets a single token repoint in index.html restyle the
// whole console. It is also what lets the Control Plane section carry its
// scoped light palette; anything that hard-codes a colour silently opts out of
// both, so don't.
//
// Two rules carried over from `src/controlPlane/ui.tsx`, which prototyped this
// vocabulary for one section before it was promoted app-wide:
//
//   1. NO STATE IS CARRIED BY COLOUR ALONE. Every chip states a word. Colour
//      is the second signal, never the only one.
//   2. DEV / TEST / PRODUCT MUST BE IMPOSSIBLE TO CONFUSE, and an unknown lane
//      says UNKNOWN rather than assuming the safest-sounding answer.

import type { CSSProperties, ReactNode } from "react";
import { IconAlert, IconChevronRight, IconInfo } from "./icons";
import { TONE_BG, TONE_BORDER, TONE_FG, statusTone, type Tone } from "./tone";

export type { Tone };

// ─── Chip ───────────────────────────────────────────────────────────────

/** A state, stated. The word is the signal; the colour agrees with it. */
export function Chip({
  tone = "neutral",
  children,
  title,
  size = "md",
  solid = false,
}: {
  tone?: Tone;
  children: ReactNode;
  title?: string;
  size?: "sm" | "md";
  solid?: boolean;
}) {
  return (
    <span
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: size === "sm" ? "1px 7px" : "3px 9px",
        fontSize: size === "sm" ? 10.5 : 11.5,
        fontWeight: 620,
        letterSpacing: 0.02,
        lineHeight: 1.5,
        whiteSpace: "nowrap",
        borderRadius: "var(--r-pill)",
        border: `1px solid ${TONE_BORDER[tone]}`,
        background: solid ? TONE_BG[tone] : "transparent",
        color: TONE_FG[tone],
      }}
    >
      {children}
    </span>
  );
}

/** A chip whose tone is derived from the backend status string. `label`
 *  overrides the displayed text without changing which tone is chosen. */
export function StatusChip({
  status,
  label,
  size,
}: {
  status: string | null | undefined;
  label?: string;
  size?: "sm" | "md";
}) {
  const text = label ?? (status ? status.replace(/_/g, " ") : "unknown");
  return (
    <Chip tone={statusTone(status)} size={size} solid>
      {text.toUpperCase()}
    </Chip>
  );
}

// ─── Card ───────────────────────────────────────────────────────────────

export function Card({
  children,
  title,
  note,
  actions,
  tone,
  padded = true,
  style,
}: {
  children?: ReactNode;
  title?: ReactNode;
  note?: ReactNode;
  actions?: ReactNode;
  tone?: Tone;
  padded?: boolean;
  style?: CSSProperties;
}) {
  return (
    <section
      className="g-card"
      style={{ borderColor: tone ? TONE_BORDER[tone] : undefined, ...style }}
    >
      {(title !== undefined || actions !== undefined) && (
        <header
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            padding: "12px 16px",
            borderBottom: "1px solid var(--border-faint)",
          }}
        >
          <div style={{ flex: "1 1 auto", minWidth: 0 }}>
            {title !== undefined && (
              <div
                style={{
                  fontSize: 13.5,
                  fontWeight: 620,
                  color: tone ? TONE_FG[tone] : "var(--fg)",
                }}
              >
                {title}
              </div>
            )}
            {note !== undefined && (
              <div style={{ fontSize: 12.5, color: "var(--fg-dim)", marginTop: 2 }}>{note}</div>
            )}
          </div>
          {actions !== undefined && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {actions}
            </div>
          )}
        </header>
      )}
      {children !== undefined && <div style={{ padding: padded ? "16px" : 0 }}>{children}</div>}
    </section>
  );
}

/** A headline number with its exact machine value underneath.
 *
 *  `value` is the operator-facing rendering ("1,842.50 USD"); `raw` is the
 *  exact integer the backend actually holds ("184,250 minor"). Both are
 *  always shown: the human value is what a person reads, the raw value is
 *  what they quote in a ticket, and dropping either has burned an operator
 *  before. */
export function StatCard({
  label,
  value,
  raw,
  hint,
  chip,
  tone,
  icon,
  href,
  cta,
}: {
  label: string;
  value: ReactNode;
  raw?: ReactNode;
  hint?: ReactNode;
  chip?: ReactNode;
  tone?: Tone;
  icon?: ReactNode;
  href?: string;
  cta?: ReactNode;
}) {
  return (
    <section
      className="g-card"
      style={{
        padding: "16px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        borderTop: `2px solid ${tone ? TONE_FG[tone] : "var(--border-strong)"}`,
        minWidth: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        {icon !== undefined && (
          <span style={{ color: tone ? TONE_FG[tone] : "var(--fg-dim)", display: "flex" }}>
            {icon}
          </span>
        )}
        <span
          style={{
            flex: "1 1 auto",
            fontSize: 11.5,
            fontWeight: 650,
            letterSpacing: 0.06,
            textTransform: "uppercase",
            color: "var(--fg-dim)",
            minWidth: 0,
          }}
        >
          {label}
        </span>
        {chip}
      </div>
      <div
        className="g-num"
        style={{ fontSize: 26, fontWeight: 600, letterSpacing: -0.02, lineHeight: 1.2 }}
      >
        {value}
      </div>
      {raw !== undefined && (
        <div className="g-num" style={{ fontSize: 11.5, color: "var(--fg-dim)" }}>
          {raw}
        </div>
      )}
      {hint !== undefined && (
        <div style={{ fontSize: 12.5, color: "var(--fg-dim)", marginTop: 2 }}>{hint}</div>
      )}
      {(cta !== undefined || href !== undefined) && (
        <div style={{ marginTop: "auto", paddingTop: 10 }}>{cta}</div>
      )}
    </section>
  );
}

// ─── Buttons ────────────────────────────────────────────────────────────

/** `primary` is a FILLED button and there should be exactly one per screen —
 *  that is the whole point of the variant. `danger` is outlined rather than
 *  filled: a destructive action should be unmistakable but should not be the
 *  brightest thing on the page, because the brightest thing is where the eye
 *  goes first and that must not be "delete". */
export function Button({
  children,
  onClick,
  variant = "default",
  size = "md",
  disabled,
  type = "button",
  title,
  full,
  icon,
  style,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: "default" | "primary" | "danger" | "ghost";
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  type?: "button" | "submit";
  title?: string;
  full?: boolean;
  icon?: ReactNode;
  style?: CSSProperties;
}) {
  const pad =
    size === "lg" ? "13px 22px" : size === "sm" ? "5px 10px" : "8px 14px";
  const font = size === "lg" ? 15 : size === "sm" ? 12.5 : 13.5;

  const variantStyle: CSSProperties =
    variant === "primary"
      ? {
          background: "var(--accent)",
          borderColor: "var(--accent)",
          color: "var(--accent-fg)",
          fontWeight: 650,
        }
      : variant === "danger"
        ? {
            background: "var(--danger-bg)",
            borderColor: "var(--danger-border)",
            color: "var(--danger)",
          }
        : variant === "ghost"
          ? { background: "transparent", borderColor: "transparent", color: "var(--fg-dim)" }
          : {};

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: pad,
        fontSize: font,
        borderRadius: "var(--r-sm)",
        width: full ? "100%" : undefined,
        ...variantStyle,
        ...style,
      }}
    >
      {icon}
      {children}
    </button>
  );
}

// ─── Banner ─────────────────────────────────────────────────────────────

/** A standing condition of the console — the lane it targets, a tripped
 *  breaker, an action that will move money. Short by construction: a banner
 *  that needs a paragraph is a `Disclosure` with a one-line summary. */
export function Banner({
  tone = "info",
  title,
  children,
  actions,
  compact = false,
  role,
}: {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  compact?: boolean;
  /** Overrides the implicit live region. Pass `"none"` for a STANDING
   *  condition — something that is simply true about the screen rather than
   *  news that just arrived. A page may hold only one live region: twenty
   *  assertions across nine test files match getByRole("alert"/"status") in
   *  the singular and throw on a second match. */
  role?: "alert" | "status" | "none";
}) {
  const isAlert = tone === "danger" || tone === "warn";
  const resolvedRole =
    role === "none" ? undefined : (role ?? (isAlert ? "alert" : undefined));
  return (
    <div
      role={resolvedRole}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: compact ? "9px 11px" : "12px 14px",
        border: `1px solid ${TONE_BORDER[tone]}`,
        background: TONE_BG[tone],
        borderRadius: "var(--r-md)",
        fontSize: compact ? 12 : 13,
        color: "var(--fg-muted)",
      }}
    >
      <span style={{ color: TONE_FG[tone], display: "flex", flex: "0 0 auto", marginTop: 1 }}>
        {isAlert ? <IconAlert size={compact ? 14 : 16} /> : <IconInfo size={compact ? 14 : 16} />}
      </span>
      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
        {title !== undefined && (
          <div style={{ color: TONE_FG[tone], fontWeight: 650, marginBottom: children ? 2 : 0 }}>
            {title}
          </div>
        )}
        {children}
      </div>
      {actions !== undefined && <div style={{ flex: "0 0 auto" }}>{actions}</div>}
    </div>
  );
}

/** The console's standing error surface. Kept as `role="alert"` with a
 *  literal "Retry" affordance because several page tests assert exactly
 *  that pair — and because an error an operator cannot retry from is a
 *  dead end. */
export function ErrorNote({
  children,
  onRetry,
}: {
  children: ReactNode;
  onRetry?: () => void;
}) {
  return (
    <Banner
      tone="danger"
      actions={
        onRetry ? (
          <Button size="sm" onClick={onRetry}>
            Retry
          </Button>
        ) : undefined
      }
    >
      {children}
    </Banner>
  );
}

// ─── Disclosure ─────────────────────────────────────────────────────────

/** Where the technical detail goes.
 *
 *  Nothing is deleted by this redesign; the verbose material moves in here.
 *  Native `<details>` so it is keyboard-operable, screen-reader-announced and
 *  findable by the browser's own in-page search WITHOUT any JS — an operator
 *  hitting ⌘F for a request id still finds it inside a closed panel in
 *  browsers that support `hidden="until-found"` semantics, and can always
 *  open it with a keystroke. */
export function Disclosure({
  summary,
  count,
  children,
  defaultOpen = false,
  tone = "neutral",
}: {
  summary: ReactNode;
  count?: number;
  children: ReactNode;
  defaultOpen?: boolean;
  tone?: Tone;
}) {
  return (
    <details className="g-disclosure g-card" open={defaultOpen} style={{ overflow: "hidden" }}>
      <summary>
        <IconChevronRight size={14} className="g-caret" />
        <span style={{ color: tone === "neutral" ? undefined : TONE_FG[tone] }}>{summary}</span>
        {count !== undefined && (
          <span style={{ color: "var(--fg-dim)", fontWeight: 500 }}>({count})</span>
        )}
      </summary>
      <div style={{ padding: "4px 16px 16px", borderTop: "1px solid var(--border-faint)" }}>
        {children}
      </div>
    </details>
  );
}

// ─── Key / value ────────────────────────────────────────────────────────

export function KvList({ children, dense }: { children: ReactNode; dense?: boolean }) {
  return (
    <dl
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(120px, max-content) 1fr",
        gap: dense ? "4px 14px" : "9px 18px",
        margin: 0,
        fontSize: 13,
      }}
    >
      {children}
    </dl>
  );
}

export function Kv({ k, children }: { k: ReactNode; children: ReactNode }) {
  return (
    <>
      <dt style={{ color: "var(--fg-dim)", margin: 0 }}>{k}</dt>
      <dd style={{ margin: 0, wordBreak: "break-word", color: "var(--fg-muted)" }}>{children}</dd>
    </>
  );
}

/** A hash or address is unreadable in full and ambiguous at four characters.
 *  Eight and eight, with the whole value in the title attribute. */
export function Hash({ value, chars = 8 }: { value: string | null | undefined; chars?: number }) {
  if (!value) return <span style={{ color: "var(--fg-dim)" }}>—</span>;
  const short =
    value.length > chars * 2 + 4
      ? `${value.slice(0, chars)}…${value.slice(-chars)}`
      : value;
  return (
    <code title={value} className="g-mono">
      {short}
    </code>
  );
}

// ─── Empty ──────────────────────────────────────────────────────────────

export function EmptyState({
  title,
  children,
  icon,
  action,
}: {
  title: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 8,
        padding: "48px 24px",
        color: "var(--fg-dim)",
      }}
    >
      {icon !== undefined && (
        <span
          style={{
            display: "flex",
            padding: 12,
            borderRadius: "var(--r-pill)",
            background: "var(--bg-inset)",
            border: "1px solid var(--border-faint)",
            color: "var(--fg-dim)",
          }}
        >
          {icon}
        </span>
      )}
      <div style={{ color: "var(--fg)", fontSize: 15, fontWeight: 600 }}>{title}</div>
      {children !== undefined && (
        <div style={{ fontSize: 13, maxWidth: "46ch" }}>{children}</div>
      )}
      {action !== undefined && <div style={{ marginTop: 8 }}>{action}</div>}
    </div>
  );
}

export function LoadingBlock({ label = "Loading…" }: { label?: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "40px 24px",
        color: "var(--fg-dim)",
        fontSize: 13,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 13,
          height: 13,
          borderRadius: "50%",
          border: "2px solid var(--border-strong)",
          borderTopColor: "var(--accent)",
          animation: "g-spin 700ms linear infinite",
        }}
      />
      {label}
      <style>{"@keyframes g-spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}

// ─── Layout helpers ─────────────────────────────────────────────────────

/** The standard responsive card grid. `min` sets the point at which a column
 *  is dropped rather than squeezed. */
export function CardGrid({
  children,
  min = 264,
  gap = 14,
}: {
  children: ReactNode;
  min?: number;
  gap?: number;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
        // Cards size to their own content. Stretching every card in a row to
        // the tallest one turns a short card into a mostly-empty box, which
        // an operator reads as missing data rather than as less data.
        alignItems: "start",
        gap,
      }}
    >
      {children}
    </div>
  );
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11.5,
        fontWeight: 650,
        letterSpacing: 0.06,
        textTransform: "uppercase",
        color: "var(--fg-dim)",
      }}
    >
      {children}
    </div>
  );
}

/** Vertical rhythm. Every page stacks its sections through this so the
 *  spacing between a header, a stat row and a table is decided once. */
export function Stack({
  children,
  gap = 18,
  style,
}: {
  children: ReactNode;
  gap?: number;
  style?: CSSProperties;
}) {
  return <div style={{ display: "flex", flexDirection: "column", gap, ...style }}>{children}</div>;
}
